import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { resolveBack } from '../../utils/backOrigin';
import { PencilIcon, PaperAirplaneIcon, BriefcaseIcon, XCircleIcon, ExclamationTriangleIcon, ChevronDownIcon, ArrowDownTrayIcon, ArchiveBoxIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import DropdownMenu from '../ui/DropdownMenu';
import { Card } from '../ui/Card';
import { Chip } from '../ui/Chip';
import StatusPill, { SignaturePill } from '../ui/StatusPill';
import { DetailPageHeader } from '../ui/DetailPageHeader';
import { LockedAtAcceptancePill } from './LockedAtAcceptancePill';
import MarkAsLostModal from './MarkAsLostModal';
// parseApiError/isDocumentLocked: retained for future use if 409 handling is needed
import { formatQuoteNumber } from '../../utils/displayId';
import { formatCurrency } from '../../utils/formatCurrency';
import { humanizeEnum } from '../../utils/humanize';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import BackLink from '../ui/BackLink';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { SkeletonCard } from '../ui/Skeleton';
import { useConfirm, EmptyState } from '../ui';
import { MASTER_TAX_DISCLAIMER } from '../../constants/disclaimers';
import api from '../../services/api';
import { PRODUCT_CATEGORIES } from '../../quote-builder/seedCatalog';
import { isOptionLine } from '../../quote-builder/separateOptions';
import {
  readEngineLine,
  resolveMarginContext,
  detectWireDisagreement,
} from '../../quote-builder/readEngineLine';
import { resolveDisplayStatus } from '../../quote-builder';
import { acceptedQuoteView, acceptedSnapshotUnavailable } from '../../quote-builder/acceptedView';
import { useSettingsContext } from '../../hooks/useSettings';
// SentQuoteView loaded lazily below (§3c)
import SentQuoteRightPanel from './SentQuoteRightPanel';

// PT-53 §53.3b (2026-07-30, D-044): the display category vocabulary must
// accept BOTH the seed-catalog ids (`cc_spray_foam`, `oc_spray_foam`,
// `fg_blow`, `kraft_batts`, … — the write-side categoryIds) AND the legacy
// display keys (`spray_foam`, `fiberglass`, …) that older quotes may still
// carry. Building the map from `PRODUCT_CATEGORIES` gives us the seed-catalog
// side as a derived value — single source of truth — while the legacy
// entries preserve back-compat with any pre-existing DynamoDB rows.
//
// Unknown ids fall through the humanize step (snake_case → Title Case), so
// even a future seed-catalog addition renders as something readable rather
// than the pre-fix universal "Other".
const SEED_CATEGORY_LABELS = PRODUCT_CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.label;
  return acc;
}, {});
const LEGACY_CATEGORY_LABELS = {
  spray_foam: 'Spray Foam',
  fiberglass: 'Fiberglass',
  bats: 'Batts',
  cellulose: 'Cellulose',
  flow: 'Flow',
  accessories: 'Accessories',
  labor: 'Labor',
  other: 'Other',
};
const CATEGORY_LABELS = { ...LEGACY_CATEGORY_LABELS, ...SEED_CATEGORY_LABELS };
const getCategoryLabel = (cat) => CATEGORY_LABELS[cat] || (cat ? cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Other');
// IDI-132: which quotes QuoteDetail hands to the locked builder.
export function lockedLandingRedirect(quote) {
  if (!quote || quote.convertedJobId || quote.converted_job_id) return false;
  const s = String(quote.status || '').toLowerCase();
  if (s === 'lost' || s === 'expired' || s === 'archived') return false;
  const signed = (quote.signatureStatus || quote.signature_status) === 'acknowledged';
  return s === 'sent' || s === 'won' || s === 'accepted' || signed;
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // IDI-157: origin for Back, captured once at mount (query-param updates
  // navigate without state).
  const [back] = useState(() => resolveBack(location.state, { href: '/quotes', label: 'Back to Quotes' }));
  const confirm = useConfirm();
  // PT-53A W1.7 (DEF-099): settings context supplies `getEnumLabel` for the
  // Unit column (`sq_ft` → "Sq Ft") and general vocab labelling.
  const { getEnumLabel } = useSettingsContext();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [changesDialog, setChangesDialog] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [settings, setSettings] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQuote(); fetchVersions(); fetchSettings(); }, [id]);

  // §2 wiring (DEF-141b): `?action=convert` opens the ConvertToJobModal.
  // QuotesPage row action "Convert to Job" navigates to `/quotes/:id?action=convert`;
  // without this trigger the query param was silently ignored. We wait for the
  // quote to load AND for `canConvert` to be known (derived from signatureStatus
  // — which is on the quote) before firing so handleConvert routes correctly.
  const convertActionParam = searchParams.get('action');
  useEffect(() => {
    if (!quote) return;
    if (convertActionParam !== 'convert') return;
    // IDI-132: a locked-landing quote is redirected by <Navigate> below
    // (straight to Record Client Decision for ?action=convert).
    if (lockedLandingRedirect(quote)) return;
    // Fire the same handler the in-page button uses. Then clear the query
    // param so a refresh does not re-open the modal.
    handleConvert();
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, convertActionParam]);

  const fetchQuote = async () => {
    try { setLoading(true); const data = await api.getQuote(id); setQuote(acceptedQuoteView(data)); /* IDI-416: an accepted quote shows what was accepted */ }
    catch { setError('Failed to load quote.'); }
    finally { setLoading(false); }
  };

  const fetchVersions = async () => {
    try {
      const data = await api.getQuoteVersions(id);
      setVersions(data?.versions || []);
    } catch {
      // PT-33.6 (2026-07-27): surface the failure. Version history is
      // non-critical (page still renders), but a silent empty-array hides
      // a real API error from the operator. Show a transient error; keep
      // versions as [] so the tab renders gracefully.
      setVersions([]);
      setError('Failed to load version history.');
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings(data);
    } catch { /* settings fetch failure is non-fatal */ }
  };

  // Q-1: Activation now happens via Review & Send flow

  // PT-53A W2.1 (DEF-103): Mark Lost now routes through the well-wired
  // MarkAsLostModal (same modal QuotesPage uses — D-10, PT-52-F1) instead
  // of the inline Dialog that dropped the reason. The prior implementation
  // was a data-integrity defect on an accepted-quote-boundary action:
  // `api.markQuoteAsLost(id)` was called with NO reason argument, so every
  // loss reason typed on the detail page vanished before it hit the wire.
  const [showMarkLostModal, setShowMarkLostModal] = useState(false);

  const handleMarkAsLost = () => {
    setShowMarkLostModal(true);
  };

  // Modal emits `{ reason, alsoArchive }` — the composed reason string
  // includes both the dropdown label and any free-text notes. That's the
  // audit-of-record `DEC-05` requires.
  // §3al (2026-08-18, 348_ Q6): Lost ALWAYS archives. QuoteDetail's outer
  // catch already un-swallows archive failures (mark-as-lost is inside the
  // same try block as archive). Preserving that behavior; just removing
  // the alsoArchive gate since it's always true now.
  const handleSubmitMarkLost = async ({ reason, alsoArchive: _ignoredButPreservedForCompat }) => {
    try {
      setActionLoading(true);
      await api.markQuoteAsLost(id, reason);
      // Auto-archive per §3al — no longer optional. A failure here now
      // surfaces via the outer catch instead of being invisible.
      await api.archiveQuote(id);
      setShowMarkLostModal(false);
      await fetchQuote();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[QuoteDetail] Mark Lost failed:', err);
      setError('Failed to mark as lost. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // PT-53A W2.1 (DEF-103, reverse path): Lost → Active reactivation.
  // The state machine already permits this transition
  // (`backend-go/internal/statemachine/statemachine.go`); the previous
  // PT-49 comment said "a lost quote that comes back should be duplicated"
  // but the operator ruling for W2 (DEC-05 — recovery) supersedes: offer
  // the reverse path directly. Duplicate remains available separately.
  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      await api.activateQuote(id);
      await fetchQuote();
    } catch {
      setError('Failed to reactivate quote.');
    } finally {
      setActionLoading(false);
    }
  };

  // IDI-132 (D-01): Mark Unsent retired. A sent or signed quote now opens in
  // the builder, locked, where Reopen and Edit is the only way to undo it.

  // PT-49: Send navigates to ReviewAndSendShell
  const handleSend = () => {
    navigate(`/quotes/${id}/review`);
  };

  // PT-49: Unarchive with confirmation dialog
  const handleUnarchive = async () => {
    if (!(await confirm({ title: 'Unarchive quote?', message: 'Unarchive this quote? It will return to its previous state.', confirmLabel: 'Unarchive' }))) return;
    try { setActionLoading(true); await api.unarchiveQuote(id); await fetchQuote(); }
    catch { setError('Failed to unarchive quote.'); }
    finally { setActionLoading(false); }
  };

  // PT-08 (2026-07-22): Archive re-added per operator feedback. (Prior A9
  // comments removed by PT-14 D2 — they described an interim state where
  // Archive was gone; PT-08 restored it and PT-14 D2 adds the banner.)
  const handleArchive = async () => {
    if (!(await confirm({ title: 'Archive quote?', message: 'Archive this quote? It will be hidden from the active list.', confirmLabel: 'Archive' }))) return;
    try { setActionLoading(true); await api.archiveQuote(id); navigate('/quotes'); }
    catch { setError('Failed to archive quote.'); }
    finally { setActionLoading(false); }
  };


  // IDI-275 (2026-09-09): handleDeleteAttachment removed — the Attachments
  // card that called it is deleted from this surface. Delete affordance moves
  // to the builder's Files tab (IDI-289).

  // eslint-disable-next-line no-unused-vars
  const handleSendForSignature = async () => { // kept for future 'Send for Signature' Other-Actions item
    if (!(await confirm({ title: 'Send for signature?', message: 'Send this quote for customer signature?', confirmLabel: 'Send' }))) return;
    try {
      setActionLoading(true);
      const res = await api.sendForSignature(id);
      const data = res.data || res;
      // IDI-14: the backend now emails the customer directly and returns an
      // ABSOLUTE signature_link (it used to return a relative "/signature/{token}"
      // which is why this once prepended window.location.origin — doing that now
      // would produce "https://apphttps://app/...").
      //
      // delivery_status tells the user what actually happened. "sent" is not a
      // safe assumption: 29% of contacts have no email on file, and in dev the
      // mailer guard redirects anything outside the allowlist.
      if (data.delivery_status === 'sent') {
        await confirm({
          title: 'Sent for signature',
          message: `We emailed the signature request to ${data.sent_to}.`,
          confirmLabel: 'OK',
        });
      } else if (data.signature_link) {
        const why = data.delivery_status === 'no_recipient'
          ? 'This customer has no contact email on file, so we could not send it.'
          : data.delivery_status === 'redirected'
            ? `Test environment: this went to the test inbox (${data.sent_to}), not to the customer.`
            : `We could not email this (${data.delivery_detail || data.delivery_status}).`;
        await confirm({
          title: 'Signature link generated — not emailed',
          message: `${why}\n\nSend this link to the customer:\n${data.signature_link}`,
          confirmLabel: 'OK',
        });
      }
      await fetchQuote();
    } catch { setError('Failed to send for signature.'); }
    finally { setActionLoading(false); }
  };

  const handleRequestChanges = async () => {
    try { setActionLoading(true); await api.requestQuoteChanges(id, { notes: changeSummary }); setChangesDialog(false); setChangeSummary(''); await fetchQuote(); }
    catch { setError('Failed to request changes.'); }
    finally { setActionLoading(false); }
  };

  // Money-path 38.3: Convert to Job requires a recorded acceptance (client
  // signature acknowledged, or contractor recorded decision via PT-38). Without
  // an AcceptedConfiguration, the backend refuses. UI gates match backend.
  // PT-49: Convert to Job — always enabled on sent quotes. Routes to Record
  // Convert to Job — ONE conversion surface (operator ruling 08-09 §2).
  //
  // Always route to /record-decision. That page is the selection surface AND
  // the conversion surface: user reviews/toggles alternates + options, then
  // clicks "Approve & Convert" which produces the accepted-configuration
  // snapshot the backend requires and creates the job.
  //
  // Previously we opened ConvertToJobModal to collect selections, then hit
  // the backend which rejected acknowledged-without-snapshot quotes with a
  // 500. The modal was doing the wrong half of the work; /record-decision
  // does both halves.
  const handleConvert = () => {
    navigate(`/quotes/${id}/record-decision`);
  };

  // PT-49: handleMarkAsSent removed — replaced by handleSend (navigates to ReviewAndSendShell)

  const handleDownloadPDF = async () => {
    try {
      setActionLoading(true);
      const res = await api.generateQuotePDF(id);
      const data = res?.data || res;
      // N-14: canonical key is pdf_base64 (same as QuotesPage.js:271).
      const b64 = data?.pdf_base64;
      const url = data?.url;
      if (b64) {
        const blob = new Blob(
          [Uint8Array.from(atob(b64), c => c.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = data?.filename || `quote-${formatQuoteNumber(quote)}.pdf`;
        a.click();
        URL.revokeObjectURL(objUrl);
      } else if (url) {
        window.open(url, '_blank');
      } else if (data instanceof Blob) {
        const objUrl = URL.createObjectURL(data);
        window.open(objUrl, '_blank');
      } else {
        setError('PDF generated but could not be downloaded.');
      }
    } catch { setError('Failed to generate PDF.'); }
    finally { setActionLoading(false); }
  };


  if (loading) return <div className="space-y-4 p-6"><SkeletonCard /><SkeletonCard /></div>;
  // IDI-132 (D-01): a sent quote, or a signed / won quote with no job, opens
  // as the full quote in the builder, locked. No separate view. Lost, expired
  // and archived keep SentQuoteView; a converted quote keeps View Job here.
  if (quote && lockedLandingRedirect(quote)) {
    return (
      <Navigate
        replace
        to={convertActionParam === 'convert' ? `/quotes/${id}/record-decision` : `/quotes/${id}/edit`}
        state={location.state}
      />
    );
  }
  if (!quote) return (
    // PR-43 (§3): mirrors JobDetail's not-found shape — alert + BackLink in a
    // wrapper div. Previously a bare Alert with no route forward.
    <div className="px-6">
      <Alert variant="error">Quote not found.</Alert>
      <div className="mt-4">
        <BackLink to="/quotes" label="Back to Quotes" />
      </div>
    </div>
  );

  const lineItems = quote.line_items || quote.lineItems || [];
  const subtotal = quote.subtotal ?? lineItems.reduce((s, i) => s + (i.line_total ?? i.lineTotal ?? 0), 0);
  const taxRate = quote.taxRate || quote.tax_rate || (settings?.tax_rate) || 0;
  const taxAmount = quote.taxAmount ?? quote.tax_amount ?? Math.round(subtotal * taxRate);
  const total = subtotal + taxAmount;

  // DEF-081 (W0.1b/c): count effective line items across both display modes so
  // the empty-state check + inconsistency notice work in Flat Mode AND when the
  // record uses areas/subAreas. `lineItems` above only reflects the Flat-Mode
  // top-level field; Areas Mode stashes items inside `quote.areas[].subAreas[].lineItems`.
  const areasList = Array.isArray(quote.areas) ? quote.areas : [];
  const areasItemCount = areasList.reduce(
    (sum, a) => sum + (Array.isArray(a.subAreas) ? a.subAreas : []).reduce(
      (sSum, sa) => sSum + (Array.isArray(sa.lineItems) ? sa.lineItems.length : 0),
      0,
    ),
    0,
  );
  const effectiveItemCount = lineItems.length + areasItemCount;
  // W0.1c ruling (operator, 2026-07-29): render EmptyState + subtotal as stored
  // + a visible inconsistency notice. Never silently reconcile a contradiction
  // on a money surface (§silent-success is worse than visible failure).
  const hasSubtotalWithoutItems = effectiveItemCount === 0 && subtotal > 0;

  // PT-53A W1.7 (DEF-099): quote-level marginContext, resolved ONCE per
  // render. Every line — Flat Mode AND Areas Mode — uses this context via
  // `readEngineLine`, so "the same line" evaluates identically on both
  // surfaces (W1.7c cross-surface invariant).
  const marginContext = resolveMarginContext(quote, quote.customer || null);

  // PT-53A DEF-115 (2026-07-29): the reconciliation notice must fire on
  // ANY header-vs-lines mismatch — both display modes, exact-cents
  // tolerance. W1.7d's original implementation gated `headerSubtotalDrifts`
  // on `hasAreas`, which meant Flat-Mode drift (Q-834444 shape:
  // $4,940.00 in lines under a $28,320.00 header) went silent.
  //
  // The class-of-failure lesson (operator, 2026-07-29): our fixtures
  // describe the bug we just found rather than the class it belongs to.
  // W1.7 fixtured empty (QF-AREAS-02) and Areas-mismatch (implicit) but
  // NOT populated-Flat-mismatch. QF-RECON-01 (new this cycle) covers the
  // gap by construction.
  //
  // Sum engine-derived line totals across BOTH shapes:
  //   • Flat: `quote.line_items[]` (already resolved into `lineItems` above)
  //   • Areas: `quote.areas[].subAreas[].lineItems[]`
  // The lines-of-truth for the reconciliation check is the union.
  const allAreasLines = areasList.flatMap((a) =>
    (Array.isArray(a.subAreas) ? a.subAreas : []).flatMap((sa) =>
      Array.isArray(sa.lineItems) ? sa.lineItems : [],
    ),
  );
  // Q-15 (2026-08-18): filter out OPTION lines before summing. Same defect
  // as QuoteReviewSend's reconciliationWarning — options are opt-in
  // (§3k) and NEVER counted in the quote total; the banner was comparing
  // headerCents (excludes options) against enginePerLineSum (includes them)
  // and firing on the difference. `wireDisagreements` uses the full
  // `allLines` set because per-line wire-vs-computed drift is independent
  // of option-inclusion.
  const allLines = [...lineItems, ...allAreasLines];
  const baseLines = allLines.filter((li) => !isOptionLine(li));
  const enginePerLineSum = baseLines.reduce(
    (s, li) => s + readEngineLine(li, marginContext).total,
    0,
  );
  // Wire-vs-computed disagreement per line — still a defect signal, unchanged.
  const wireDisagreements = allLines
    .map((li) => detectWireDisagreement(li, marginContext))
    .filter(Boolean);

  // Header-reconciliation fires on ANY of three shapes:
  //   (a) DEF-081 legacy: positive subtotal, zero lines.
  //   (b) Header ≠ sum of engine-derived per-line totals — with ±1 cent
  //       tolerance to match detectWireDisagreement (DEF-115 fix; tolerance
  //       added 2026-07-31 after false-positive reports on fresh quotes).
  //       The exact-cents comparison tripped on any float rounding drift
  //       during read — in practice the only meaningful signal is deltas
  //       larger than half-cent rounding.
  //   (c) Any per-line wire `line_total` disagrees with independent recompute.
  //
  // subtotal is coerced through Number() so a string wire value still compares.
  //
  // Fresh-quote defence: also require quote.subtotal to be explicitly present
  // on the wire. If it's absent (falsy), the fallback `sum(line_total)` is
  // BY DEFINITION equal to itself — comparing it to enginePerLineSum tests
  // wire-vs-recompute per line, which wireDisagreements already covers.
  // Firing headerSubtotalDrifts on the fallback-derived subtotal is a
  // tautology that produces false positives when line_total is present
  // but subtotal isn't (the fresh-save shape).
  const headerCents = Number(subtotal) || 0;
  const RECONCILIATION_TOLERANCE_CENTS = 1;
  const wireHasExplicitSubtotal = Number.isFinite(Number(quote.subtotal))
    && Number(quote.subtotal) > 0;
  const headerSubtotalDrifts =
    allLines.length > 0
    && wireHasExplicitSubtotal
    && Math.abs(headerCents - enginePerLineSum) > RECONCILIATION_TOLERANCE_CENTS;
  const hasReconciliationNotice =
    hasSubtotalWithoutItems || headerSubtotalDrifts || wireDisagreements.length > 0;
  // Adaptive copy — always visible, never silent (§silent-success rule).
  let reconciliationNoticeCopy;
  if (hasSubtotalWithoutItems) {
    reconciliationNoticeCopy =
      'This quote has a subtotal but no line items. The record may be incomplete — open the quote builder and verify the line items before sending.';
  } else if (headerSubtotalDrifts) {
    reconciliationNoticeCopy =
      `This quote's line items don't reconcile with its stored subtotal. Open the quote builder and re-save to update the totals.`;
  } else if (wireDisagreements.length > 0) {
    reconciliationNoticeCopy =
      `One or more line totals on this quote don't match what the engine derives from their inputs. Open the quote builder and re-save to recalculate.`;
  }

  const statusHistory = quote.statusHistory || quote.status_history || [];
  const linkedJobId = quote.convertedJobId || quote.converted_job_id;

  // PT-53 §53.4c (2026-07-30, D-049): sent+expired status collapse. When a
  // quote is still `sent` in the backend but the `valid_until` date has
  // passed, resolve to `expired` for display. Prior to this the header
  // rendered a SENT status pill next to a red "(Expired)" indicator in the
  // Expires field — a visible contradiction. Backend status is left
  // untouched; the collapse is display-only. This mirrors the collapse the
  // landing table already applies.
  // DEF-019: route through resolveDisplayStatus so draft→Active mapping
  // is applied consistently (same as builder, landing, dashboard).
  const displayStatus = (() => {
    let effective = quote.status;
    if (effective === 'sent') {
      const validUntil = quote.validUntil || quote.valid_until;
      if (validUntil) {
        const expiresAt = new Date(validUntil);
        if (Number.isFinite(expiresAt.getTime()) && expiresAt.getTime() < Date.now()) {
          effective = 'expired';
        }
      }
    }
    return resolveDisplayStatus(effective, (quote.lineItems || quote.line_items || []).length);
  })();

  return (
    <div className="px-6">
      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
      {/* PT-14 D2 (Wave 3, 2026-07-24): archived-quote banner. Explains why
          the quote is read-only and points at Reactivate as the reverse. */}
      {quote.status === 'archived' && (
        <Alert variant="info" className="mb-4">
          This quote is archived. Reactivate to make changes.
        </Alert>
      )}

      {/* PT-53A Tier-2 (DEF-113): Lost-quote banner. The loss reason is
          captured on `mark_as_lost` (backend stores as `lostReason`) but
          was never rendered anywhere in the frontend — "a reason you
          can't read is a reason you don't have" on an audit-of-record
          action. Surface it as an info banner alongside Archived.
          Falls back to a generic message if the reason is missing on
          legacy records (pre-DEF-103 marks) rather than showing an
          empty banner. */}
      {quote.status === 'lost' && (
        <Alert variant="info" className="mb-4">
          {quote.lostReason || quote.lost_reason ? (
            <>This quote was marked Lost. Reason: <strong>{quote.lostReason || quote.lost_reason}</strong></>
          ) : (
            <>This quote was marked Lost. No reason was recorded on this record.</>
          )}
        </Alert>
      )}

      {/* IDI-22 (2026-08-11 ruling, gate step): a migrated quote with no
          stored total was recomputed from its line items rather than shown
          as $0.00 or left blank. Flag it as derived — not verified/sent by
          the dealer — until the dealer opens and saves the quote (which
          replaces total_source with a real recompute through the margin
          engine, clearing this banner per the ruling's requirement). */}
      {(quote.total_source || quote.totalSource) && (
        <Alert variant="warning" className="mb-4">
          This total was reconstructed from line items and has not been verified. Review before sending.
        </Alert>
      )}
      {/* IDI-416: never fall back to the live quote when the accepted record
        * can't be read; say so instead. */}
      {acceptedSnapshotUnavailable(quote) && (
        <Alert variant="warning" className="mb-4">
          The accepted version of this quote couldn&apos;t be loaded. The lines below are the current quote, not what was accepted.
        </Alert>
      )}

      {/* Header — R17-37: Back-affordance ABOVE title.
          PT-43.2 (2026-07-28): LockedAtAcceptancePill rendered when the quote
          has acceptance-lock provenance, explaining *why* pricing is frozen
          BEFORE the user tries and fails. Show on signatureStatus=acknowledged
          OR when acceptedConfigurationId is stamped. */}
      <DetailPageHeader
        // IDI-157: Back = the page (and tab) the user came from; with no
        // in-app origin, the section default. A converted quote never
        // reaches this page (ConvertedQuoteGuard sends it to its job).
        backHref={back.href}
        backState={back.state}
        backLabel={back.label}
        title={formatQuoteNumber(quote)}
        status={displayStatus}
        subtitle={quote.customerName || quote.customer_name}
        pills={
          (quote.signatureStatus === 'acknowledged' ||
            quote.signature_status === 'acknowledged' ||
            quote.acceptedConfigurationId ||
            quote.accepted_configuration_id)
            ? (
              <LockedAtAcceptancePill
                lockedTotalCents={quote.lockedTotalCents ?? quote.locked_total_cents ?? quote.total_amount}
                acceptedConfigurationId={quote.acceptedConfigurationId || quote.accepted_configuration_id}
                acceptedAt={
                  // IDI-370: the e-sign path writes signatureAcknowledgedAt;
                  // Record Decision writes acknowledgedAt.
                  quote.signatureAcknowledgedAt || quote.signature_acknowledged_at ||
                  quote.acknowledgedAt || quote.acknowledged_at
                }
                quoteId={quote.id}
              />
            )
            : undefined
        }
      />
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {(quote.version || 1) > 1 && <Chip color="info">v{quote.version}</Chip>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* PT-49: State-specific actions per operator ruling 2026-07-29.
              State A (Active/Unsent): primary = Edit Quote. More: Send · Download PDF · Mark Lost · Archive.
              State B (Sent): primary = Convert to Job (ALWAYS enabled). More: Resend · Download PDF · Mark Lost · Archive.
              Lost: no primary. More: Archive.
              Archived: no primary. More: Unarchive.
              Won/Accepted (signed, not converted — IDI-533/D-01): primary = Convert to Job.
                More: Download PDF · Archive.
              Converted (linkedJobId): View Job only. */}
          {(() => {
            const rawStatus = quote.status;
            const isA = rawStatus === 'draft' || rawStatus === 'active';
            const isLost = rawStatus === 'lost' || rawStatus === 'expired';
            const isArchived = rawStatus === 'archived';

            if (isA) {
              // IDI-133 (2026-09-01): Convert to Job now available from state A
              // (active/draft), not just state B (sent). Signature no longer
              // gates. Convert placed after Send/Download so destructive actions
              // (Lost/Archive) still cluster at the bottom, matching state B's
              // menu shape. Guard on !linkedJobId prevents double-convert.
              const menuItems = [
                { label: 'Send', icon: PaperAirplaneIcon, onClick: handleSend },
                { label: 'Download PDF', icon: ArrowDownTrayIcon, onClick: handleDownloadPDF },
                !linkedJobId && { label: 'Convert to Job', icon: BriefcaseIcon, onClick: handleConvert },
                { label: 'Mark Lost', icon: XCircleIcon, onClick: handleMarkAsLost, danger: true },
                { label: 'Archive', icon: ArchiveBoxIcon, onClick: handleArchive },
              ].filter(Boolean);
              return (
                <>
                  <Button onClick={() => navigate(`/quotes/${id}/edit`)} loading={actionLoading}>
                    <PencilIcon className="h-4 w-4" />Edit Quote
                  </Button>
                  <DropdownMenu
                    ariaLabel="Other actions"
                    trigger={<Button variant="secondary">Other Actions<ChevronDownIcon className="h-4 w-4 ml-1" /></Button>}
                    panelClassName="w-52"
                    items={menuItems.map((item) => ({
                      label: item.label,
                      icon: <item.icon className="h-4 w-4" />,
                      onClick: item.onClick,
                      variant: item.danger ? 'danger' : 'default',
                    }))}
                  />
                </>
              );
            }

            // IDI-132 (D-01): a sent quote, and a signed / won quote with no
            // job, redirect to the locked builder above; the Mark Unsent and
            // signed-quote menus that lived here are gone. A sent or won quote
            // that HAS a job falls through to View Job only.

            if (isLost) {
              // PT-53A W2.1 (DEF-103, reverse path): Lost state now offers
              // Reactivate as a primary CTA. The backend state machine
              // (statemachine.go) already permits Lost → Active. The prior
              // "no Reopen/Reactivate; duplicate instead" PT-49 ruling is
              // superseded by DEC-05 (recovery). Duplicate remains available
              // via the general menu; Reactivate is the direct reverse path.
              return (
                <>
                  <Button variant="secondary" onClick={handleReactivate} loading={actionLoading}>
                    <PencilIcon className="h-4 w-4" />Reactivate
                  </Button>
                  <DropdownMenu
                    ariaLabel="Other actions"
                    trigger={<Button variant="secondary">Other Actions<ChevronDownIcon className="h-4 w-4 ml-1" /></Button>}
                    panelClassName="w-52"
                    items={[{
                      label: 'Archive',
                      icon: <ArchiveBoxIcon className="h-4 w-4" />,
                      onClick: handleArchive,
                    }]}
                  />
                </>
              );
            }

            if (isArchived) {
              // PT-49: Archived state — Unarchive with confirmation.
              return (
                <Button variant="secondary" onClick={handleUnarchive} loading={actionLoading}>
                  Unarchive
                </Button>
              );
            }

            // Converted state (status='converted' OR linkedJobId present on any status):
            // show View Job as the only CTA. DEC-56: converted quotes are excluded
            // from listing surfaces; the detail page is the only way in (via direct URL
            // or the invoice/job rail link). The user needs a way back to the job.
            if (linkedJobId) {
              return (
                <Button variant="secondary" onClick={() => navigate(`/jobs/${linkedJobId}`)}>
                  <BriefcaseIcon className="h-4 w-4" />View Job
                </Button>
              );
            }

            return null;
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* §3c: sent/lost/expired/archived → read-only customer document */}
          {['sent', 'lost', 'expired', 'archived'].includes((quote.status || '').toLowerCase()) ? (
            <React.Suspense fallback={<div className="animate-pulse h-96 bg-gray-100 rounded" />}>
              {React.createElement(React.lazy(() => import('./SentQuoteView')), { quote, customer: null })}
            </React.Suspense>
          ) : (
          <>
          {/* Line Items */}
          <Card>
            {/* Areas Mode Display */}
            {(quote.areas && quote.areas.length > 0) ? (
              <div className="p-4 space-y-4">
                {quote.areas.map((area, areaIdx) => {
                  // DEF-099 (W1.7b): area subtotal is the sum of engine-derived
                  // line totals — not a wire read. Same helper that drives the
                  // per-line Total cell (readEngineLine) so the subtotal ALWAYS
                  // agrees with the sum of its rendered lines by construction.
                  const areaSubtotal = (area.subAreas || []).reduce((saSum, sa) => {
                    return saSum + (sa.lineItems || []).reduce(
                      (liSum, li) => liSum + readEngineLine(li, marginContext).total,
                      0,
                    );
                  }, 0);
                  return (
                    <div key={area.id || areaIdx} className="border border-[color:var(--color-border)]" style={{ borderRadius: 'var(--radius-md)' }}>
                      {/* Area Header */}
                      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-[color:var(--color-border)]">
                        <h4 className="font-semibold text-sm text-gray-800">{area.name}</h4>
                        <span className="text-sm font-medium text-gray-600">{formatCurrency(areaSubtotal)}</span>
                      </div>
                      {/* Sub-Areas */}
                      {(area.subAreas || []).map((subArea, saIdx) => (
                        <div key={subArea.id || saIdx} className="px-4 py-2 border-b border-gray-100 last:border-b-0">
                          <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{subArea.name}</h5>
                          {/* DEF-081 (W0.1b): subArea-scoped empty state. A money surface must
                              never assert a header above zero rows. Copy per operator ruling
                              (2026-07-29): "No line items on this quote." scoped down to subArea. */}
                          {(!Array.isArray(subArea.lineItems) || subArea.lineItems.length === 0) ? (
                            <EmptyState
                              mode="first-run"
                              icon={ClipboardDocumentListIcon}
                              title="No line items in this sub-area."
                              description="Add items in the builder to see them here."
                              className="py-6"
                            />
                          ) : (
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableHeader>Item</TableHeader>
                                <TableHeader align="right">Qty</TableHeader>
                                <TableHeader>Unit</TableHeader>
                                <TableHeader align="right">Cost</TableHeader>
                                <TableHeader align="right">Margin</TableHeader>
                                <TableHeader align="right">Rate</TableHeader>
                                <TableHeader align="right">Total</TableHeader>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(subArea.lineItems || []).map((item, liIdx) => {
                                // DEF-099 (W1.7a/b): Areas Mode routes every line
                                // through `readEngineLine` (which calls
                                // `computeLineItem`). Money is derived ONCE, in the
                                // engine, from the line's own inputs — never in a
                                // render function, and never trusted to arrive
                                // pre-computed.
                                //
                                // Three flags govern the render:
                                //   • hasWirePrice     — wire supplied a positive stored total
                                //   • hasWireCost      — wire supplied a resolvable cost basis
                                //   • hasExplainedMargin — the margin figure is trustworthy
                                //     (not the 100%-by-back-calculation artefact)
                                //
                                // When a field is untrusted, render '—'. `$0.00` implies
                                // a priced zero-value line — do NOT use it as a
                                // fabrication for "missing input".
                                const eng = readEngineLine(item, marginContext);

                                const displayName =
                                  item.item_name
                                  ?? item.product_name
                                  ?? item.productName
                                  ?? item.description
                                  ?? '—';
                                // Unit column: enum key → human label via getEnumLabel.
                                const rawUnit = item.unit ?? item.unitOfMeasure ?? item.unit_of_measure;
                                const displayUnit = rawUnit
                                  ? (getEnumLabel && getEnumLabel('unit_of_measure', rawUnit)) || rawUnit
                                  : '—';
                                // Rate: display-only (never a multiplicand). Prefer
                                // unit_rate (rounded), fall back to unit_price.
                                const displayRate =
                                  item.unit_rate
                                  ?? item.unitRate
                                  ?? item.unit_price
                                  ?? item.unitPrice;
                                // Cost / Margin / Total — all engine-derived:
                                const costDisplay = eng.hasWireCost ? formatCurrency(eng.cost) : '—';
                                const marginDisplay = eng.hasExplainedMargin
                                  ? `${Math.round(eng.margin * 1000) / 10}%`
                                  : '—';
                                const totalDisplay = eng.hasWirePrice
                                  ? formatCurrency(eng.total)
                                  : (eng.hasWireCost && eng.hasExplainedMargin ? formatCurrency(eng.total) : '—');
                                return (
                                  <TableRow key={item.id || liIdx}>
                                    <TableCell>{displayName}</TableCell>
                                    {/* DEF-114 parity with Flat Mode: quantity cell renders
                                        the number when finite, '—' when absent. */}
                                    <TableCell align="right">
                                      {item.quantity != null && item.quantity !== '' && Number.isFinite(Number(item.quantity))
                                        ? item.quantity
                                        : '—'}
                                    </TableCell>
                                    <TableCell>{displayUnit}</TableCell>
                                    <TableCell align="right" className="text-gray-500 text-xs">{costDisplay}</TableCell>
                                    <TableCell align="right" className="text-gray-500 text-xs">{marginDisplay}</TableCell>
                                    <TableCell align="right">{Number.isFinite(Number(displayRate)) ? formatCurrency(Number(displayRate)) : '—'}</TableCell>
                                    <TableCell align="right" className="font-medium">{totalDisplay}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Flat Mode Display */
              /* DEF-081 (W0.1b): render EmptyState when no line items. Money surfaces
                 must never assert a header above zero rows. Copy per operator ruling
                 (2026-07-29 W0 answers): "No line items on this quote." */
              lineItems.length === 0 ? (
                <EmptyState
                  mode="first-run"
                  icon={ClipboardDocumentListIcon}
                  title="No line items on this quote."
                  description="Open the builder to add scope, materials, and labor."
                  className="py-10"
                />
              ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Item</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader align="right">Qty</TableHeader>
                    {/* PT-53A Tier-2 (DEF-114): Unit was previously coalesced
                        into the Qty cell as `{quantity} {unit}`. On records
                        where quantity was falsy, that rendered just the unit
                        ("Sq Ft") in the Qty column — the number was absent.
                        Split into its own column, matching Areas Mode's layout. */}
                    <TableHeader>Unit</TableHeader>
                    <TableHeader align="right">Cost</TableHeader>
                    <TableHeader align="right">Margin</TableHeader>
                    <TableHeader align="right">Rate</TableHeader>
                    <TableHeader align="right">Total</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, idx) => {
                    // DEF-099 (W1.7a/c): Flat Mode uses the SAME readEngineLine
                    // projection as Areas Mode. Cross-surface invariant: same
                    // wire line → identical rendered money on both surfaces.
                    const eng = readEngineLine(item, marginContext);
                    const rawUnit = item.unit ?? item.unitOfMeasure ?? item.unit_of_measure;
                    const displayUnit = rawUnit
                      ? (getEnumLabel && getEnumLabel('unit_of_measure', rawUnit)) || rawUnit
                      : '';
                    const displayRate =
                      item.unit_rate
                      ?? item.unitRate
                      ?? item.unit_price
                      ?? item.unitPrice;
                    const costDisplay = eng.hasWireCost ? formatCurrency(eng.cost) : '—';
                    const marginDisplay = eng.hasExplainedMargin
                      ? `${Math.round(eng.margin * 1000) / 10}%`
                      : '—';
                    const totalDisplay = eng.hasWirePrice
                      ? formatCurrency(eng.total)
                      : (eng.hasWireCost && eng.hasExplainedMargin ? formatCurrency(eng.total) : '—');
                    return (
                      <TableRow key={item.id || idx}>
                        <TableCell>
                          <p className="font-medium">{item.item_name || item.description}</p>
                          {item.description && item.item_name && <p className="text-xs text-gray-500">{item.description}</p>}
                          {item.notes && <p className="text-xs text-gray-400 italic mt-0.5">{item.notes}</p>}
                          {/* PT-14 G1a (Wave 3, 2026-07-24): ALT + MAT chips tokenized to match OPT pattern. */}
                          {(item.is_alternate || item.isAlternate) && <span className="text-xs px-1 rounded" style={{ backgroundColor: 'var(--color-status-alt-bg)', color: 'var(--color-status-alt-text)' }}>ALT</span>}
                          {(item.is_option || item.isOption) && <span className="text-xs px-1 rounded ml-1" style={{ backgroundColor: 'var(--color-status-option-bg)', color: 'var(--color-status-option-text)' }}>OPT</span>}
                          {(item.material_only || item.materialOnly) && <span className="text-xs px-1 rounded ml-1" style={{ backgroundColor: 'var(--color-status-neutral-bg)', color: 'var(--color-status-neutral-text)' }}>MAT</span>}
                        </TableCell>
                        <TableCell><Chip color="default">{getCategoryLabel(item.category)}</Chip></TableCell>
                        {/* DEF-114: quantity is its own cell; falsy quantity renders '—'
                            rather than blending with the unit. */}
                        <TableCell align="right">
                          {item.quantity != null && item.quantity !== '' && Number.isFinite(Number(item.quantity))
                            ? item.quantity
                            : '—'}
                        </TableCell>
                        <TableCell>{displayUnit || '—'}</TableCell>
                        <TableCell align="right" className="text-gray-500 text-xs">{costDisplay}</TableCell>
                        <TableCell align="right" className="text-gray-500 text-xs">
                          {marginDisplay}
                          {/* PT-14 G1b: margin-override amber icon (only meaningful when we have
                              a margin to show; otherwise the icon would sit next to a '—'). */}
                          {eng.hasExplainedMargin && (item.margin_override || item.marginOverride) && (
                            <ExclamationTriangleIcon className="inline h-3.5 w-3.5 ml-0.5" style={{ color: 'var(--color-warning-fill)' }} title={item.override_reason || item.overrideReason || ''} />
                          )}
                        </TableCell>
                        <TableCell align="right">{Number.isFinite(Number(displayRate)) ? formatCurrency(Number(displayRate)) : '—'}</TableCell>
                        <TableCell align="right" className="font-semibold">{totalDisplay}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              )
            )}

            {/* Accessories */}
            {(quote.accessories && quote.accessories.length > 0) && (
              <div className="border-t border-[color:var(--color-border)] px-4 py-3">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Accessories</h4>
                {quote.accessories.map((acc, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    {/* DEF-097 (W1.6e): accessory row. Prefer item_name (canonical);
                        display line_total directly, do NOT derive by arithmetic.
                        The `qty × rate` fragment stays as a display-only breakdown —
                        the total on the right MUST render the stored line_total when
                        present, and '—' when absent (never a computed guess). */}
                    <span>{acc.item_name || acc.product_name || acc.productName || '—'}</span>
                    <span>
                      {acc.quantity} × {formatCurrency(Number(acc.unit_rate ?? acc.unitRate ?? acc.unit_price ?? acc.unitPrice) || 0)}
                      {' = '}
                      {Number.isFinite(Number(acc.line_total ?? acc.lineTotal))
                        ? formatCurrency(Number(acc.line_total ?? acc.lineTotal))
                        : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}


            {/* DEF-081 (W0.1c): inconsistency notice per operator ruling (2026-07-29).
                When the quote asserts a non-zero subtotal but has no line items, do NOT
                silently reconcile the contradiction. Render the stored subtotal unchanged
                AND flag the inconsistency so a human catches it before the record is sent.
                Applies to both Flat Mode and Areas Mode; check uses effectiveItemCount. */}
            {/* DEF-090 (W1.5a): Alert owns its variant icon; do NOT pass an
                icon child. The prior W0.1c commit rendered an
                ExclamationTriangleIcon inside the Alert body, which
                produced two icons (Alert's own + caller's) side-by-side.
                Icon-count invariant now enforced by the DEF-081 test
                (W1.5c). */}
            {/* DEF-099 (W1.7d): reconciliation notice fires on any of three shapes:
                (a) positive subtotal + zero line items (the DEF-081 shape);
                (b) Areas Mode + header subtotal doesn't equal the sum of
                    engine-derived per-line totals (±1¢);
                (c) any per-line wire `line_total` disagrees with what the
                    engine independently derives from the line's inputs.
                Copy adapts to which shape fired. Never silent. */}
            {hasReconciliationNotice && (
              <Alert variant="warning" className="mx-4 mb-3">
                {reconciliationNoticeCopy}
              </Alert>
            )}
            <div className="border-t border-[color:var(--color-border)] px-4 py-3 space-y-1">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
              <div className="flex justify-between text-sm text-gray-500"><span>Tax{taxRate ? ` (${(taxRate * 100).toFixed(2)}%)` : ''}</span><span>{formatCurrency(taxAmount)}</span></div>
              <div className="flex justify-between text-base font-bold pt-1 border-t border-gray-100"><span>Total</span><span>{formatCurrency(total)}</span></div>
            </div>
            <p className="px-4 pb-3 text-xs text-gray-400 italic">{MASTER_TAX_DISCLAIMER}</p>
          </Card>

          {/* IDI-275 (2026-09-09): T&C card removed from QuoteDetail per Adrian's
              ruling — T&C display belongs ONLY on Review & Send. */}

          {/* IT-R4-003: Disclaimer display */}
          {(quote.disclaimer) && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Disclaimer</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{quote.disclaimer}</p>
            </Card>
          )}
          </>)}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Details</h3>
            <div><p className="text-xs text-gray-500">Property Type</p><p className="text-sm">{humanizeEnum(quote.property_type || quote.propertyType) || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Created</p><p className="text-sm">{(() => { const d = quote.created_at || quote.createdAt; if (!d) return '—'; try { const date = new Date(d); return isNaN(date.getTime()) ? '—' : `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`; } catch { return '—'; } })()}</p></div>
            {quote.title && <div><p className="text-xs text-gray-500">Title</p><p className="text-sm">{quote.title}</p></div>}
            {/* WP2: Salesperson */}
            {/* DEF-082 (W0.2b, 2026-07-29): the block ALWAYS renders. "Field
                absent" and "field empty" are different bugs per bounce §4.2;
                the previous conditional (PT-53 §53.4a) hid both cases under
                the same disappearance, which is the §silent-success failure.
                Three distinguishable states:
                  (1) name resolves — render the name
                  (2) only a UUID present (pre-PT-52 record, or write path
                      missing) — render UUID in muted style with '(name unresolved)'
                      so the operator can see the raw ID and act on it
                  (3) genuinely unassigned — render '— Unassigned' in muted style */}
            {(() => {
              const name =
                quote.salespersons?.[0]?.name ||
                quote.salespersonName ||
                quote.salesperson_name ||
                '';
              const id =
                quote.salespersons?.[0]?.id ||
                quote.salespersonId ||
                quote.salesperson_id ||
                (Array.isArray(quote.salesperson_ids) && quote.salesperson_ids[0]) ||
                '';
              // W0.2c backfill flag: dev-console warn when an id is stamped
              // but no salespersons[] shape exists to resolve the name. This is
              // the DEF-015 backfill class — forward-only writer, backward reader.
              if (id && !name && !Array.isArray(quote.salespersons) && typeof console !== 'undefined') {
                // eslint-disable-next-line no-console
                console.warn(
                  '[DEF-082/DEF-015] Salesperson id stamped without salespersons[] resolution; likely pre-PT-52 record. quote.id=%s id=%s',
                  quote.id,
                  id,
                );
              }
              if (name) {
                return (
                  <div>
                    <p className="text-xs text-gray-500">Salesperson</p>
                    <p className="text-sm">{name}</p>
                  </div>
                );
              }
              if (id) {
                return (
                  <div>
                    <p className="text-xs text-gray-500">Salesperson</p>
                    <p className="text-sm text-gray-500" title="Salesperson ID is stamped on this record but no name resolves. This is usually a pre-migration record.">
                      {id} <span className="text-xs italic">(name unresolved)</span>
                    </p>
                  </div>
                );
              }
              return (
                <div>
                  <p className="text-xs text-gray-500">Salesperson</p>
                  <p className="text-sm text-gray-400 italic">— Unassigned</p>
                </div>
              );
            })()}
            {/* WP4: Valid Until with expiry warning */}
            {(quote.validUntil || quote.valid_until) && (
              <div>
                <p className="text-xs text-gray-500">Expires</p>
                <p className="text-sm">
                  {(() => {
                    const d = quote.validUntil || quote.valid_until;
                    if (!d) return '—';
                    const date = new Date(d);
                    const now = new Date();
                    const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
                    const formatted = `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`;
                    if (daysUntil < 0) return <span className="text-red-600 font-medium">{formatted} (Expired)</span>;
                    if (daysUntil <= 7) return <span className="text-amber-600 font-medium">{formatted} ({daysUntil}d left)</span>;
                    return formatted;
                  })()}
                </p>
              </div>
            )}
            {/* WP6: Signature Status */}
            {(quote.signatureStatus || quote.signature_status) && (
              <div>
                {/* DEF-122 (2026-08-03): explicit `Signature:` prefix disambiguates
                    from the adjacent quote-status pill; SignaturePill wrapper for
                    N-13-correct tone selection (was: raw StatusPill). */}
                <p className="text-xs text-gray-500">Signature</p>
                <SignaturePill signatureStatus={quote.signatureStatus || quote.signature_status} />
              </div>
            )}
            {linkedJobId && <div><p className="text-xs text-gray-500">Linked Job</p><p className="text-sm"><a href={`/jobs/${linkedJobId}`} className="text-primary-600 hover:underline">{quote.convertedJobNumber || quote.converted_job_number || linkedJobId}</a></p></div>}
          </Card>

          {/* PR-56 (347_ §4, Wave B B3): sent-page right panel — contractor-
              facing, STATIC. Names the T&C template attached at send time and
              lists attachments as links. Add/remove controls are deliberately
              absent on this surface. IDI-275 (2026-09-09) removed the
              working-view Attachments/T&C/Notes cards from QuoteDetail per
              Adrian's ruling; the builder's Files tab (IDI-289) is now the
              edit surface for attachments. Only
              renders on the sent-page surface (`sent|lost|expired|archived`,
              matching the SentQuoteView lazy-mount predicate at L726). */}
          {['sent', 'lost', 'expired', 'archived'].includes((quote.status || '').toLowerCase()) && (
            <SentQuoteRightPanel quote={quote} />
          )}

          {/* Version History */}
          {(versions.length > 0 || statusHistory.length > 0) && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Version History</h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {/* Current version indicator */}
                <div className="border-2 border-primary-200 bg-primary-50 p-2 text-xs" style={{ borderRadius: 'var(--radius-md)' }}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary-700">v{quote.version || 1} (current)</span>
                    <StatusPill status={displayStatus} />
                  </div>
                </div>
                {/* Prior version snapshots */}
                {[...versions].sort((a, b) => (b.snapshotVersion || 0) - (a.snapshotVersion || 0)).map((ver, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="w-full text-left border border-[color:var(--color-border)] p-2 text-xs hover:bg-gray-50 transition-colors" style={{ borderRadius: 'var(--radius-md)' }}
                    onClick={() => setViewingVersion(ver)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">v{ver.snapshotVersion || ver.version || idx + 1}</span>
                      <span className="text-gray-400">{ver.snapshotAt ? new Date(ver.snapshotAt).toLocaleDateString() : ''}</span>
                    </div>
                    {ver.changeSummary && <p className="text-gray-500 mt-1 italic">"{ver.changeSummary}"</p>}
                  </button>
                ))}
                {/* Status transitions fallback */}
                {versions.length === 0 && statusHistory.length > 0 && statusHistory.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="border border-gray-100 p-2 text-xs" style={{ borderRadius: 'var(--radius-md)' }}>
                    <div className="flex items-center gap-1">
                      <StatusPill status={resolveDisplayStatus(entry.from || 'new')} />
                      <span className="text-gray-400">→</span>
                      <StatusPill status={resolveDisplayStatus(entry.to)} />
                    </div>
                    {entry.changed_at && <p className="text-gray-400 mt-1">{new Date(entry.changed_at).toLocaleString()}</p>}
                    {entry.notes && <p className="text-gray-600 mt-1 italic">"{entry.notes}"</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* IDI-275 (2026-09-09): Notes card removed from QuoteDetail per Adrian's
              ruling — notes display belongs ONLY on Review & Send. */}
        </div>
      </div>

      {/* Request Changes Dialog */}
      <Dialog open={changesDialog} onClose={() => setChangesDialog(false)}>
        <DialogHeader onClose={() => setChangesDialog(false)}>Request Changes</DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-600 mb-3">This will return the quote to Active and bump the version.</p>
          <textarea
            className="input"
            rows={4}
            placeholder="Describe what needs to change..."
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setChangesDialog(false)}>Cancel</Button>
          <Button onClick={handleRequestChanges} disabled={!changeSummary.trim()} loading={actionLoading}>Request Changes</Button>
        </DialogFooter>
      </Dialog>

      {/* Convert to Job Modal removed (2026-08-09, operator ruling §2): the
          conversion flow is now ONE surface at /quotes/:id/record-decision.
          The modal collected the same selections and hit the same endpoint;
          consolidating removes the deadlock where the modal collected
          selections but couldn't produce an accepted-configuration snapshot. */}

      {/* Version Viewer Dialog */}
      <Dialog open={!!viewingVersion} onClose={() => setViewingVersion(null)}>
        <DialogHeader>Version {viewingVersion?.snapshotVersion || viewingVersion?.version} (Read-Only)</DialogHeader>
        <DialogBody>
          {viewingVersion && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Status:</span> <StatusPill status={resolveDisplayStatus(viewingVersion.status)} /></div>
                <div><span className="text-gray-500">Date:</span> {viewingVersion.snapshotAt ? new Date(viewingVersion.snapshotAt).toLocaleDateString() : '—'}</div>
                <div><span className="text-gray-500">Customer:</span> {viewingVersion.customerName || viewingVersion.customer_name || '—'}</div>
                <div><span className="text-gray-500">Property:</span> {humanizeEnum(viewingVersion.propertyType || viewingVersion.property_type) || '—'}</div>
              </div>
              {viewingVersion.changeSummary && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm">
                  <span className="font-medium">Change Summary:</span> {viewingVersion.changeSummary}
                </div>
              )}
              {(viewingVersion.line_items || viewingVersion.lineItems || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Line Items</h4>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Item</TableHeader>
                        <TableHeader>Category</TableHeader>
                        <TableHeader align="right">Qty</TableHeader>
                        <TableHeader align="right">Rate</TableHeader>
                        <TableHeader align="right">Total</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewingVersion.line_items || viewingVersion.lineItems || []).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.item_name || item.itemName || '—'}</TableCell>
                          <TableCell>{getCategoryLabel(item.category)}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_rate ?? item.unitRate)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.line_total ?? item.lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="border-t pt-2 text-right">
                <p className="text-sm">Subtotal: {formatCurrency(viewingVersion.subtotal ?? 0)}</p>
                {(viewingVersion.taxAmount ?? viewingVersion.tax_amount) > 0 && <p className="text-sm text-gray-500">Tax: {formatCurrency(viewingVersion.taxAmount ?? viewingVersion.tax_amount)}</p>}
                {/* IDI-215: total_amount is canonical (the live, editable
                    value the builder writes on every save); totalAmount is a
                    stale legacy alias derived from bidAmountCents at
                    migration time and can disagree with total_amount once a
                    quote has actually been edited in BIDIT 2.0. Prefer
                    total_amount first — the ticket's reported symptom was
                    exactly this precedence being backwards. */}
                <p className="font-bold">Total: {formatCurrency(viewingVersion.total_amount ?? viewingVersion.totalAmount ?? viewingVersion.subtotal ?? 0)}</p>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setViewingVersion(null)}>Close</Button>
        </DialogFooter>
      </Dialog>

      {/* PT-53A W2.1 (DEF-103): Mark Lost modal — SAME MarkAsLostModal that
          QuotesPage uses. Captures the composed reason (dropdown + notes)
          and forwards it to `api.markQuoteAsLost(id, reason)`. Replaces the
          inline Dialog that dropped the reason. */}
      <MarkAsLostModal
        open={showMarkLostModal}
        onCancel={() => setShowMarkLostModal(false)}
        onSubmit={handleSubmitMarkLost}
      />

    </div>
  );
}
